/**
 * correctionIssuesView — PURE view mapping of the structured correction
 * issues (PR-ONB-PRECHECK-3) to wizard steps + applicant-facing copy.
 *
 * `targetKey` vocabulary (BE contract, `fieldIssueTargets.ts`):
 *   FIELD    → draft-payload path ('organization.vatNumber', 'addresses.primary', …)
 *   DOCUMENT → document type ('VISURA_CAMERALE' / 'RC_PROFESSIONALE')
 *
 * FIELD issues map onto the wizard step that edits the path (DOCUMENT issues
 * surface on the area Documents page, which already renders the per-type
 * correction note from the DOCS-2 checklist).
 */

import type { AreaOpenIssue } from '@/lib/areaApi';
import type { WizardStepKey } from '@/lib/accountBindFlow';

export const ISSUE_REASON_LABELS: Record<AreaOpenIssue['reasonCode'], string> = {
  MISSING_VALUE: 'Dato mancante',
  INVALID_VALUE: 'Dato non valido',
  EXPIRED_DOCUMENT: 'Documento scaduto',
  UNREADABLE_DOCUMENT: 'Documento illeggibile',
  WRONG_DOCUMENT: 'Documento errato',
  INCONSISTENT_DATA: 'Dati incoerenti',
  OTHER: 'Altro',
};

export const ISSUE_TARGET_LABELS: Record<string, string> = {
  'organization.legalName': 'Denominazione / nome',
  'organization.fiscalCode': 'Codice fiscale organizzazione',
  'organization.vatNumber': 'Partita IVA',
  'organization.businessLegalForm': 'Forma giuridica',
  'organization.ruiNumber': 'Numero RUI',
  'organization.ruiRegistrationDate': 'Data iscrizione RUI',
  'organization.sdiCode': 'Codice SDI',
  'organization.withholdingType': "Regime ritenuta d'acconto",
  'adminUser.fullName': 'Nome e cognome referente',
  'adminUser.fiscalCode': 'Codice fiscale referente',
  'adminUser.sex': 'Sesso referente',
  'adminUser.dateOfBirth': 'Data di nascita referente',
  'adminUser.birthCountry': 'Paese di nascita referente',
  'adminUser.birthMunicipalityCadastralCode': 'Comune di nascita referente',
  'adminUser.placeOfBirth': 'Luogo di nascita referente',
  'adminUser.mobilePhone': 'Cellulare referente',
  'adminUser.ruiNumber': 'RUI personale del responsabile',
  'adminUser.ruiRegistrationDate': 'Data iscrizione RUI responsabile',
  // Composite PERSON identity targets (org IS the person — coupled leaves).
  'personIdentity.fullName': 'Nome e cognome (persona fisica)',
  'personIdentity.fiscalCode': 'Codice fiscale (persona fisica)',
  'addresses.primary': 'Indirizzo principale',
  'addresses.alternateOffice': 'Sede operativa',
  'addresses.mailingAddress': 'Indirizzo di corrispondenza',
  'addresses.alternateResidence': 'Domicilio',
  'orgContacts.email': 'Email organizzazione',
  'orgContacts.pec': 'PEC',
  'orgContacts.phone': 'Telefono fisso',
  'orgContacts.mobile': 'Cellulare organizzazione',
  VISURA_CAMERALE: 'Visura camerale',
  RC_PROFESSIONALE: 'Polizza RC professionale',
};

export function issueTargetLabel(targetKey: string): string {
  return ISSUE_TARGET_LABELS[targetKey] ?? targetKey;
}

export function issueReasonLabel(reason: AreaOpenIssue['reasonCode']): string {
  return ISSUE_REASON_LABELS[reason] ?? reason;
}

/**
 * Wizard step(s) that edit a FIELD target. Most targets map to a single step;
 * `organization.withholdingType` lives on the dedicated "fiscale" step.
 *
 * COMPOSITE PERSON identity targets (`personIdentity.*`) span BOTH the
 * organization step (where a persona fisica enters the name/CF that ARE the
 * org identity) AND the amministratore step (the coupled referente leaf), so
 * the banner is shown on both — the candidate must update both to resolve the
 * coupled correction. DOCUMENT targets return [] (handled on the area
 * Documents page).
 */
export function stepsForIssueTarget(
  issue: Pick<AreaOpenIssue, 'targetType' | 'targetKey'>,
): WizardStepKey[] {
  if (issue.targetType === 'DOCUMENT') return [];
  const key = issue.targetKey;
  if (key === 'organization.withholdingType') return ['fiscale'];
  if (key.startsWith('personIdentity.')) return ['organizzazione', 'amministratore'];
  if (key.startsWith('organization.')) return ['organizzazione'];
  if (key.startsWith('orgContacts.')) return ['contatti'];
  if (key.startsWith('addresses.')) return ['indirizzi'];
  if (key.startsWith('adminUser.')) return ['amministratore'];
  return [];
}

/** FIELD issues belonging to the given wizard step (composites span two). */
export function issuesForStep(
  step: WizardStepKey,
  issues: readonly AreaOpenIssue[],
): AreaOpenIssue[] {
  return issues.filter((issue) => stepsForIssueTarget(issue).includes(step));
}

/** DOCUMENT issues (surfaced on the area Documents page / status page). */
export function documentIssues(
  issues: readonly AreaOpenIssue[],
): AreaOpenIssue[] {
  return issues.filter((issue) => issue.targetType === 'DOCUMENT');
}
